import React from 'react'
import { Box, Typography, Stack } from '@mui/material'
import { styled } from '@mui/material/styles'
import { drawerWidth } from 'materialUI/jss/material-dashboard-pro-react'
import { BookingStep } from '../hooks/useBookingWizard'

const STEPS: { key: BookingStep; label: string }[] = [
  { key: 'journey', label: 'Journey' },
  { key: 'vehicle', label: 'Vehicle' },
  { key: 'confirmation', label: 'Confirm' },
]

const TopBarContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: drawerWidth,
  right: 0,
  height: 70,
  backgroundColor: theme.palette.brand.dark,
  zIndex: 100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingLeft: 24,
  paddingRight: 24,
  boxShadow: theme.shadows[2],
}))

const BrandText = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: 4,
  textTransform: 'uppercase',
}))

const StepContainer = styled(Box)({
  display: 'flex',
  gap: 24,
  alignItems: 'center',
})

interface StepIndicatorProps {
  $isActive: boolean
  $isCompleted: boolean
  $stepNumber: number
}

const StepCircle = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== '$isActive' && prop !== '$isCompleted' && prop !== '$stepNumber',
})<StepIndicatorProps>(({ theme, $isActive, $isCompleted }) => ({
  width: 32,
  height: 32,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  fontWeight: 700,
  transition: 'all 0.2s ease',
  backgroundColor: $isActive
    ? theme.palette.secondary.main
    : $isCompleted
      ? theme.palette.success.main
      : theme.palette.brand.light,
  color: $isActive || $isCompleted ? theme.palette.common.white : theme.palette.text.secondary,
  cursor: 'pointer',
  '&:hover': {
    transform: 'scale(1.05)',
  },
}))

const StepLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== '$isActive',
})<{ $isActive: boolean }>(({ theme, $isActive }) => ({
  fontSize: 12,
  fontWeight: 500,
  color: $isActive ? theme.palette.secondary.main : theme.palette.text.disabled,
  transition: 'color 0.2s ease',
  userSelect: 'none',
}))

const UserInfo = styled(Stack)({
  alignItems: 'flex-end',
  gap: 2,
})

const UserName = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 600,
  color: theme.palette.common.white,
}))

const AccountName = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  color: theme.palette.text.secondary,
  opacity: 0.8,
}))

interface TopBarProps {
  currentStep: BookingStep
  onStepClick: (step: BookingStep) => void
  userName: string
  accountName: string
}

const TopBar: React.FC<TopBarProps> = ({
  currentStep,
  onStepClick,
  userName,
  accountName,
}) => {
  const getStepIndex = (step: BookingStep): number => {
    return STEPS.findIndex((s) => s.key === step)
  }

  const currentStepIndex = getStepIndex(currentStep)

  return (
    <TopBarContainer>
      <BrandText>Bookings</BrandText>

      <StepContainer>
        {STEPS.map((step, index) => {
          const isActive = step.key === currentStep
          const isCompleted = index < currentStepIndex

          return (
            <Box
              key={step.key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <StepCircle
                $isActive={isActive}
                $isCompleted={isCompleted}
                $stepNumber={index + 1}
                onClick={() => {
                  if (index <= currentStepIndex) {
                    onStepClick(step.key)
                  }
                }}
              >
                {isCompleted ? '✓' : index + 1}
              </StepCircle>
              <StepLabel $isActive={isActive}>{step.label}</StepLabel>
            </Box>
          )
        })}
      </StepContainer>

      <UserInfo>
        <UserName>{userName}</UserName>
        <AccountName>{accountName}</AccountName>
      </UserInfo>
    </TopBarContainer>
  )
}

export default TopBar
